import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';

import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { Select } from '@/components/common/Select';
import { Textarea } from '@/components/common/Textarea';
import { TemplateTaskEditor } from '@/components/templates/TemplateTaskEditor';
import { useTemplateStore } from '@/stores/template-store';
import type { TemplateTask } from '@/types/checklist-template';
import { WeaponType } from '@/types/season';

const WEAPON_OPTIONS = [
  { value: '', label: 'Select weapon type...' },
  { value: WeaponType.Gun, label: 'Gun' },
  { value: WeaponType.Bow, label: 'Bow' },
  { value: WeaponType.Muzzleloader, label: 'Muzzleloader' },
  { value: WeaponType.Crossbow, label: 'Crossbow' },
  { value: WeaponType.Youth, label: 'Youth' },
  { value: WeaponType.Any, label: 'Any' },
  { value: WeaponType.Other, label: 'Other' },
];

interface FormErrors {
  name?: string;
  weaponType?: string;
  region?: string;
}

export function TemplateFormPage() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { templates, loadTemplates, addTemplate, updateTemplate, getTemplate } =
    useTemplateStore();

  const isEditing = id !== undefined;
  const existingTemplate = isEditing ? getTemplate(id) : undefined;

  const [name, setName] = useState(existingTemplate?.name ?? '');
  const [description, setDescription] = useState(existingTemplate?.description ?? '');
  const [weaponType, setWeaponType] = useState<WeaponType | ''>(
    existingTemplate?.weaponType ?? '',
  );
  const [region, setRegion] = useState(existingTemplate?.region ?? '');
  const [tasks, setTasks] = useState<TemplateTask[]>(existingTemplate?.tasks ?? []);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load templates if editing and store is empty
  useEffect(() => {
    if (isEditing && templates.length === 0) {
      void loadTemplates();
    }
  }, [isEditing, templates.length, loadTemplates]);

  // Sync form state once template loads from store
  useEffect(() => {
    if (existingTemplate) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot sync from external prop on mount
      setName(existingTemplate.name);
      setDescription(existingTemplate.description);
      setWeaponType(existingTemplate.weaponType);
      setRegion(existingTemplate.region);
      setTasks(existingTemplate.tasks);
    }
    // Only run when the template object reference changes (after load)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingTemplate?.id]);

  // Guard: built-in templates cannot be edited
  if (isEditing && existingTemplate?.isBuiltIn) {
    return (
      <div className="flex flex-col gap-4 py-8">
        <p className="text-body-sm text-[var(--color-text-secondary)]">
          Built-in templates cannot be edited.
        </p>
        <Link
          to={`/prep/templates/${id}`}
          className="text-body-sm text-[var(--color-accent)] hover:underline"
        >
          Back to template
        </Link>
      </div>
    );
  }

  function validate(): boolean {
    const next: FormErrors = {};
    if (!name.trim()) next.name = 'Name is required';
    if (!weaponType) next.weaponType = 'Weapon type is required';
    if (!region.trim()) next.region = 'Region is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      if (isEditing && id) {
        await updateTemplate(id, {
          name: name.trim(),
          description,
          // CAST: validate() ensures weaponType is not empty before reaching here
          weaponType: weaponType as WeaponType,
          region: region.trim(),
          tasks,
        });
        navigate(`/prep/templates/${id}`);
      } else {
        const created = await addTemplate({
          name: name.trim(),
          description,
          // CAST: validate() ensures weaponType is not empty before reaching here
          weaponType: weaponType as WeaponType,
          region: region.trim(),
          isBuiltIn: false,
          version: 1,
          tasks,
        });
        navigate(`/prep/templates/${created.id}`);
      }
    } catch (err) {
      console.error('TemplateFormPage.handleSubmit:', err);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col gap-6 pb-24">
      {/* Back link */}
      <div className="flex items-center gap-3">
        <Link
          to={isEditing && id ? `/prep/templates/${id}` : '/prep/templates'}
          className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
          aria-label={isEditing ? 'Back to template' : 'Back to templates'}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </Link>
        <h1 className="font-display text-display-lg text-[var(--color-text-display)]">
          {isEditing ? 'Edit Template' : 'New Template'}
        </h1>
      </div>

      {/* Form */}
      <form
        data-testid="template-form"
        onSubmit={(e) => void handleSubmit(e)}
        className="flex flex-col gap-6"
      >
        {/* Metadata section */}
        <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-card p-4 flex flex-col gap-4">
          <Input
            label="Template Name"
            id="template-name"
            value={name}
            onChange={setName}
            placeholder="e.g. Midwest Gun Season Prep"
            required
            error={errors.name}
          />

          <Textarea
            label="Description"
            id="template-description"
            value={description}
            onChange={setDescription}
            placeholder="Optional description of this template"
            rows={2}
          />

          <Select
            label="Weapon Type"
            id="template-weapon-type"
            value={weaponType}
            onChange={(v) => setWeaponType(v as WeaponType | '')}
            options={WEAPON_OPTIONS}
            required
            error={errors.weaponType}
          />

          <Input
            label="Region"
            id="template-region"
            value={region}
            onChange={setRegion}
            placeholder="midwest, southeast, any, etc."
            required
            error={errors.region}
          />
        </div>

        {/* Tasks section */}
        <div className="flex flex-col gap-3">
          <hr className="border-[var(--color-border)]" />
          <h2 className="font-display text-display-sm text-[var(--color-text-display)]">Tasks</h2>
          <TemplateTaskEditor tasks={tasks} onChange={setTasks} />
        </div>

        {/* Save button */}
        <div className="flex justify-end">
          <Button
            type="submit"
            variant="primary"
            disabled={isSubmitting}
            data-testid="save-template-btn"
          >
            {isSubmitting ? 'Saving...' : 'Save Template'}
          </Button>
        </div>
      </form>
    </div>
  );
}
